import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { styles } from "../../styles/IntroGuideStyles";
import guideCurrentHistory from "../../assets/guide/guide-current-history.png";
import guideCurrentOverview from "../../assets/guide/guide-current-overview.png";
import guideCurrentPackaging from "../../assets/guide/guide-current-packaging.png";
import guideCurrentPathManage from "../../assets/guide/guide-current-path-manage.png";
import guideCurrentSupportUpdate from "../../assets/guide/guide-current-support-update.png";
import guideCurrentTerminal from "../../assets/guide/guide-current-terminal.png";

type FlowNode = {
    title: string;
    text: string;
};

type GuideImage = {
    src: string;
    caption: string;
};

type Slide = {
    eyebrow: string;
    title: string;
    summary: string;
    images: GuideImage[];
    nodes: FlowNode[];
    note: string;
};

type Props = {
    show: boolean;
    onClose: () => void;
};

type MotionDirection = "next" | "prev";

export const INTRO_GUIDE_CONTENT_VERSION = "2026-08-04.3";

const INTRO_GUIDE_ANIMATION_CSS = `
@keyframes introGuideSlideNext {
    from { opacity: 0.25; transform: translateX(36px); }
    to { opacity: 1; transform: translateX(0); }
}

@keyframes introGuideSlidePrev {
    from { opacity: 0.25; transform: translateX(-36px); }
    to { opacity: 1; transform: translateX(0); }
}

@keyframes introGuidePagerPulse {
    0% { transform: translateY(0); }
    50% { transform: translateY(-2px); }
    100% { transform: translateY(0); }
}

.intro-guide-motion {
    animation-duration: 260ms;
    animation-fill-mode: both;
    animation-timing-function: cubic-bezier(0.22, 0.74, 0.22, 1);
    will-change: transform, opacity;
}

.intro-guide-motion-next {
    animation-name: introGuideSlideNext;
}

.intro-guide-motion-prev {
    animation-name: introGuideSlidePrev;
}

.intro-guide-dot-active {
    animation: introGuidePagerPulse 220ms ease-out;
}

@media (prefers-reduced-motion: reduce) {
    .intro-guide-motion,
    .intro-guide-dot-active {
        animation: none;
    }
}
`;

const slides: Slide[] = [
    {
        eyebrow: "1. 화면 구성",
        title: "배포 경로를 선택하고 작업 화면을 이동합니다",
        summary:
            "왼쪽 배포 경로에서 작업 대상을 선택하고 상단의 패키징과 터미널 탭으로 화면을 이동합니다. 경로·SSH 관리, 배포 이력 등 공통 기능은 오른쪽 상단에 모여 있습니다.",
        images: [
            { src: guideCurrentOverview, caption: "DeployKit 메인 화면" },
        ],
        nodes: [
            {
                title: "배포 경로와 작업 탭",
                text: "왼쪽 목록에서 사이트를 선택합니다. 패키징 탭에서는 변경 파일을 만들고 전송하며, 터미널 탭에서는 SSH 또는 로컬 세션을 관리합니다.",
            },
        ],
        note: "배포 경로를 마우스 오른쪽 버튼으로 누르면 배포 대상 선택, 해당 경로 관리, SSH 관리, 새 경로 등록 메뉴를 바로 사용할 수 있습니다.",
    },
    {
        eyebrow: "2. 경로 설정",
        title: "드롭다운에서 경로와 SSH 관리를 선택합니다",
        summary:
            "상단 경로·SSH 관리 메뉴에서 경로 관리 또는 SSH 관리를 선택합니다. 경로 관리에서는 새 경로 등록과 기존 경로 수정을 함께 처리합니다.",
        images: [
            { src: guideCurrentPathManage, caption: "경로 관리" },
        ],
        nodes: [
            {
                title: "경로 관리와 SSH 관리",
                text: "드롭다운의 경로 관리에서 등록·수정·삭제를 처리하고, SSH 관리에서 여러 경로가 공유할 접속 프로필을 관리합니다.",
            },
        ],
        note: "저장을 선택한 비밀번호는 Windows 사용자 계정으로 암호화되며 경로 백업 JSON에는 포함되지 않습니다. 개인키 암호는 저장하지 않습니다.",
    },
    {
        eyebrow: "3. 패키징 선택",
        title: "날짜, 버전, 파일 순서로 배포 대상을 확정합니다",
        summary:
            "시작일과 종료일을 선택하면 Git commit 또는 SVN revision을 조회합니다. 사용할 버전을 체크한 뒤 오른쪽 파일 목록에서 실제 패키지에 포함할 항목을 선택합니다.",
        images: [
            { src: guideCurrentPackaging, caption: "날짜와 변경 파일 선택" },
        ],
        nodes: [
            {
                title: "날짜 → 버전 → 파일",
                text: "달력의 연도와 월을 직접 선택할 수 있습니다. 버전과 파일의 ALL 체크박스로 전체 선택을 제어하고, 최신순 또는 경로순 정렬로 목록을 정리합니다.",
            },
        ],
        note: "Git과 SVN은 선택 버전의 변경 파일을 기준으로 처리합니다. 형상관리가 없는 LOCAL 프로젝트는 파일 수정 시간을 사용하며, 중복 파일이 있으면 사용할 버전을 한 번 더 선택합니다.",
    },
    {
        eyebrow: "4. 생성과 전송",
        title: "패키지만 만들거나 서버 전송까지 한 번에 실행합니다",
        summary:
            "파일 선택이 끝나면 패키지 추출 또는 서버 전송을 선택합니다. 두 기능 모두 같은 패키지를 만들며, 서버 전송은 SSH 연결 정보와 원격 업로드 경로를 추가로 사용합니다.",
        images: [
            { src: guideCurrentOverview, caption: "패키지 추출과 서버 전송" },
        ],
        nodes: [
            {
                title: "추출 또는 서버 전송",
                text: "패키지 추출은 ZIP 파일을 바탕화면에 생성하고 결과 폴더를 자동으로 엽니다. 서버 전송은 ZIP 내부 파일을 디렉터리 구조대로 원격 작업 경로에 전송하고 SSH 터미널을 엽니다.",
            },
        ],
        note: "Java 파일은 등록된 프로젝트 JDK를 사용해 class 산출물로 변환합니다. 서버 전송에 실패해도 생성된 로컬 패키지는 바탕화면과 배포 이력에 남아 재전송할 수 있습니다.",
    },
    {
        eyebrow: "5. 터미널과 이력",
        title: "여러 터미널 세션과 배포 결과를 이어서 관리합니다",
        summary:
            "터미널 탭에서는 SSH 세션과 로컬 PowerShell을 여러 개 열어 탭으로 전환할 수 있습니다. 배포 이력에서는 생성 및 전송 결과를 사이트별로 조회하고 다시 전송할 수 있습니다.",
        images: [
            { src: guideCurrentTerminal, caption: "탭형 터미널" },
            { src: guideCurrentHistory, caption: "배포 이력" },
        ],
        nodes: [
            {
                title: "탭형 터미널",
                text: "왼쪽 배포 경로를 선택하면 해당 SSH 세션으로 이동하거나 새 세션을 엽니다. + 메뉴에서 선택 서버의 SSH 세션 또는 로컬 PowerShell을 추가할 수 있습니다.",
            },
            {
                title: "배포 이력",
                text: "패키지 경로, 상태와 오류 내용을 확인하고 서버 전송 또는 재전송을 실행합니다. 필요 없는 이력은 개별 삭제할 수 있습니다.",
            },
        ],
        note: "서버 전송으로 열린 SSH 세션에서는 patch.sh 실행 버튼을 사용할 수 있습니다. 서버 재시작이나 점검 명령은 같은 터미널에 직접 입력합니다.",
    },
    {
        eyebrow: "6. 업데이트와 문의",
        title: "새 버전을 설치하고 필요한 로그를 바로 전달합니다",
        summary:
            "새 버전이 배포되면 현재 버전 옆에 업데이트 버튼이 나타납니다. 문의하기 메뉴에서는 GitHub, Email과 문의용 에러 로그 추출을 사용할 수 있습니다.",
        images: [
            { src: guideCurrentSupportUpdate, caption: "업데이트와 문의 메뉴" },
        ],
        nodes: [
            {
                title: "업데이트와 문제 보고",
                text: "업데이트 버튼을 누르면 설치 파일을 내려받아 자동 실행합니다. 오류 문의 시 에러 로그 추출로 생성된 ZIP 파일을 GitHub 또는 Email 문의에 첨부합니다.",
            },
        ],
        note: "사용 안내는 상단 메뉴에서 언제든 다시 열 수 있습니다. 안내 내용 버전이 변경되면 새 내용 확인을 위해 최초 1회 자동으로 다시 표시됩니다.",
    },
];

const IntroGuideModal: React.FC<Props> = ({ show, onClose }) => {
    const [slideIndex, setSlideIndex] = useState(0);
    const [motionDirection, setMotionDirection] = useState<MotionDirection>("next");
    const pointerStartRef = useRef<{ x: number; y: number } | null>(null);
    const activeSlide = slides[slideIndex];
    const isFirst = slideIndex === 0;
    const isLast = slideIndex === slides.length - 1;

    const goToSlide = useCallback(
        (targetIndex: number) => {
            const nextIndex = Math.min(Math.max(targetIndex, 0), slides.length - 1);
            if (nextIndex === slideIndex) return;

            setMotionDirection(nextIndex > slideIndex ? "next" : "prev");
            setSlideIndex(nextIndex);
        },
        [slideIndex]
    );

    const guideCards = useMemo(
        () =>
            activeSlide.images.map((image, index) => {
                const node = activeSlide.nodes[index] ?? { title: image.caption, text: "" };

                return (
                    <figure key={`${activeSlide.eyebrow}-${image.caption}`} style={styles.mediaCard}>
                        <div style={styles.mediaImageWrap}>
                            <img src={image.src} alt={image.caption} style={styles.mediaImage} />
                        </div>
                        <figcaption style={styles.mediaCaption}>
                            <div style={styles.mediaCaptionHeader}>
                                <span style={styles.mediaStepBadge}>{index + 1}</span>
                                <span style={styles.mediaTitle}>{node.title}</span>
                            </div>
                            <p style={styles.mediaDescription}>{node.text}</p>
                        </figcaption>
                    </figure>
                );
            }),
        [activeSlide]
    );

    const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
        pointerStartRef.current = { x: event.clientX, y: event.clientY };
    };

    const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
        if (!pointerStartRef.current) return;

        const diffX = event.clientX - pointerStartRef.current.x;
        const diffY = event.clientY - pointerStartRef.current.y;
        pointerStartRef.current = null;

        if (Math.abs(diffX) < 70 || Math.abs(diffX) < Math.abs(diffY) * 1.25) return;

        goToSlide(diffX < 0 ? slideIndex + 1 : slideIndex - 1);
    };

    useEffect(() => {
        if (!show) return;

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") onClose();
            if (event.key === "ArrowLeft") goToSlide(slideIndex - 1);
            if (event.key === "ArrowRight") goToSlide(slideIndex + 1);
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [show, onClose, goToSlide, slideIndex]);

    useEffect(() => {
        if (!show) return;

        setMotionDirection("next");
        setSlideIndex(0);
    }, [show]);

    if (!show) return null;

    return (
        <div
            style={styles.overlay}
            role="presentation"
            onMouseDown={(event) => {
                if (event.target === event.currentTarget) onClose();
            }}
        >
            <style>{INTRO_GUIDE_ANIMATION_CSS}</style>
            <section role="dialog" aria-modal="true" aria-label="DeployKit 사용 안내" style={styles.dialog}>
                <header style={styles.header}>
                    <div style={styles.titleWrap}>
                        <p style={styles.eyebrow}>{activeSlide.eyebrow}</p>
                        <h2 style={styles.title}>{activeSlide.title}</h2>
                    </div>
                    <button type="button" aria-label="안내 닫기" style={styles.closeBtn} onClick={onClose}>
                        ×
                    </button>
                </header>

                <div style={styles.body}>
                    <div
                        key={slideIndex}
                        className={`intro-guide-motion intro-guide-motion-${motionDirection}`}
                        style={styles.slideLayout}
                        onPointerDown={handlePointerDown}
                        onPointerUp={handlePointerUp}
                        onPointerCancel={() => {
                            pointerStartRef.current = null;
                        }}
                    >
                        <p style={styles.summary}>{activeSlide.summary}</p>
                        <div style={styles.mediaGrid}>{guideCards}</div>
                        <div style={styles.noteBox}>{activeSlide.note}</div>
                    </div>
                </div>

                <footer style={styles.footer}>
                    <div style={styles.dots} aria-label="안내 단계">
                        {slides.map((slide, index) => {
                            const isActive = index === slideIndex;

                            return (
                                <button
                                    key={slide.eyebrow}
                                    type="button"
                                    aria-current={isActive ? "step" : undefined}
                                    aria-label={`${index + 1}번 안내로 이동`}
                                    className={isActive ? "intro-guide-dot-active" : undefined}
                                    style={isActive ? styles.dotActive : styles.dot}
                                    onClick={() => goToSlide(index)}
                                />
                            );
                        })}
                    </div>

                    <div style={styles.buttonRow}>
                        <button type="button" style={styles.secondaryButton} onClick={onClose}>
                            닫기
                        </button>
                        <button
                            type="button"
                            style={isFirst ? styles.secondaryButtonDisabled : styles.secondaryButton}
                            disabled={isFirst}
                            onClick={() => goToSlide(slideIndex - 1)}
                        >
                            이전
                        </button>
                        <button
                            type="button"
                            style={styles.primaryButton}
                            onClick={() => {
                                if (isLast) {
                                    onClose();
                                    return;
                                }
                                goToSlide(slideIndex + 1);
                            }}
                        >
                            {isLast ? "시작하기" : "다음"}
                        </button>
                    </div>
                </footer>
            </section>
        </div>
    );
};

export default IntroGuideModal;
